/**
 * Language at the table.
 *
 * Poker is played in English everywhere, including by Persian speakers: fold,
 * check, raise and BB are the words players already use, and translating them
 * makes the room read as a translation rather than as a poker room. So the
 * verbs and the units stay English, and Persian carries everything that is
 * actually prose — explanations, errors, and the state of the table.
 *
 * Chip counts are shown in big blinds, which is how stack depth is actually
 * discussed. "100BB" says something a player can act on; "200" says nothing
 * until you also remember the stakes.
 */

/** Persian digits, for prose. Amounts in BB deliberately stay Latin. */
export function faNum(value: number | string): string {
  return String(value).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]!);
}

/**
 * Chips as big blinds: `100BB`, `2.5BB`, `0.5BB`.
 *
 * One decimal at most. A player reading a stack does not need more precision
 * than that, and a column of long decimals is harder to scan than it is
 * accurate.
 */
export function bb(chips: number, bigBlind: number): string {
  if (bigBlind <= 0) return `${chips}`;
  const value = chips / bigBlind;
  const text = Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '');
  return `${text}BB`;
}

/** The same value without the unit, for places that label it separately. */
export function bbValue(chips: number, bigBlind: number): string {
  if (bigBlind <= 0) return `${chips}`;
  const value = chips / bigBlind;
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '');
}

export const SUIT_GLYPHS: Record<string, string> = { s: '♠', h: '♥', d: '♦', c: '♣' };
export const RED_SUITS = new Set(['h', 'd']);

/** Street names stay English — they are the words on every hand history ever written. */
export const STREET_LABEL: Record<string, string> = {
  preflop: 'PREFLOP',
  flop: 'FLOP',
  turn: 'TURN',
  river: 'RIVER',
  complete: 'SHOWDOWN',
};

export const ACTION_LABEL: Record<string, string> = {
  fold: 'Fold',
  check: 'Check',
  call: 'Call',
  bet: 'Bet',
  raise: 'Raise',
};

export const CATEGORY_LABEL: Record<string, string> = {
  'high-card': 'High Card',
  pair: 'Pair',
  'two-pair': 'Two Pair',
  trips: 'Three of a Kind',
  straight: 'Straight',
  flush: 'Flush',
  'full-house': 'Full House',
  quads: 'Four of a Kind',
  'straight-flush': 'Straight Flush',
};
